import ApiService from "@/api/ApiService";
import {
  Construction,
  Delete,
  KeyboardArrowDown,
  KeyboardArrowRight,
  People,
  Person,
  ThumbUpSharp,
} from "@mui/icons-material";
import { CircularProgress, Box } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import { useState } from "react";
import { Fragment } from "react/jsx-runtime";

interface CollisionProject {
  id: number;
  name: string;
  number: string;
  phase: string;
  bookingIds: number[];
}

interface Collision {
  id: number;
  tag_id: number;
  tag_name: string;
  type: "CREW" | "RESOURCE" | "HUMAN";
  name: string;
  number: string | null;
  startDate: string;
  endDate: string;
  dates: string[];
  projects: CollisionProject[];
  vacations: { id: number; name: string }[];
}

export default function CollisionContainer({ query, setCalendar }) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (value: string) => {
    setOpen((prev) => {
      const newSet = new Set(prev); // Create a copy of the current Set
      if (newSet.has(value)) {
        newSet.delete(value); // Remove the value if it exists
      } else {
        newSet.add(value); // Add the value if it doesn't exist
      }
      return newSet; // Return the updated Set
    });
  };

  const queryClient = useQueryClient();

  const acceptM = useMutation<unknown, AxiosResponse, { targetId: number; type: string; dates: string[] }>({
    mutationFn:
      ApiService.acceptCollisions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getOpenRequests","collisions"],
      });
    },
  });

  const deleteM = useMutation<unknown, AxiosResponse, { bookingIds: number[] }>({
    mutationFn:
      ApiService.deleteCollisionBookings(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getOpenRequests"],
      });
    },
  });



  return query.isLoading ? (
    <CircularProgress />
  ) : query.isLoadingError ? (
    "Error"
  ) : (
    ([...((query?.data ?? []) as Collision[])]
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .map((x, i, f) => (
      <Fragment key={x.startDate + "_" + x.tag_id + "_" + x.id}>
        {i === 0 || f[i - 1].startDate !== x.startDate ? (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              color: "#555",
              p: "4px 6px",
              lineHeight: 1,
              height: "26px",
              fontWeight: "14px",
              flexShrink: 0,
              background: "#edf0f9",
              borderTop: "1px solid rgba(0,0,0,0.2)",
              borderBottom: "1px solid rgba(0,0,0,0.2)",
              boxShadow: "inset 0px -4px 5px rgba(0,0,0,0.1)",
            }}
          >
            ab {dayjs(x.startDate).format("DD.MM.YYYY")}
          </Box>
        ) : null}
        {i === 0 || f[i - 1].tag_name !== x.tag_name ? (
          <Box
            style={
              open.has(x.startDate + "-" + x.tag_id)
                ? { borderBottom: "1px solid #aaa" }
                : {}
            }
            sx={{
              position: "sticky",
              top: "26px",
              background: "white",
              userSelect: "none",
              gap: "5px",
              fontSize: "14px",
              lineHeight: 1,
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              borderTop: "1px solid #aaa",
              cursor: "pointer",
              "&:hover": {
                background: "#eee",
              },
            }}
          >
            <Box
              onClick={() => toggle(x.startDate + "-" + x.tag_id)}
              sx={{ display: "flex", alignItems: "center" }}
            >
              {open.has(x.startDate + "-" + x.tag_id) ? (
                <KeyboardArrowDown fontSize="inherit" />
              ) : (
                <KeyboardArrowRight fontSize="inherit" />
              )}
            </Box>
            <Box
              onClick={() => toggle(x.startDate + "-" + x.tag_id)}
              sx={{ flex: 1, p: "2px 0px" }}
            >
              {x.tag_name}
            </Box>
            <Box
              onClick={() =>
                setCalendar(
                  x.type === "CREW"
                    ? "CREWS"
                    : x.type === "RESOURCE"
                    ? "RESOURCES"
                    : "STAFF",
                  x.tag_id,
                  dayjs(x.startDate, "YYYY-MM-DD")
                )
              }
              sx={{
                display: "flex",
                background: "#fff",
                "&:hover": { background: "#002594", color: "white" },
                alignItems: "center",
                borderLeft: "1px solid #aaa",
                p: "0px 2px",
                alignSelf: "stretch",
              }}
            >
              {x.type === "CREW" ? (
                <People fontSize="inherit" />
              ) : x.type === "RESOURCE" ? (
                <Construction fontSize="inherit" />
              ) : (
                <Person fontSize="inherit" />
              )}
            </Box>
          </Box>
        ) : null}
        {open.has(x.startDate + "-" + x.tag_id) ? (
          <div className="collibox">
            <div className="collires">
            <div className="colliresname">{x.number ? <span className="colliresnumber">{x.number}</span> : null}
              {x.name}</div>
              <div className="colliresend">&rarr;{dayjs(x.endDate).format("DD.MM.")}</div>
              <div className="colliaccept" onClick={()=>acceptM.mutate({targetId: x.id, type: x.type, dates: x.dates})}><ThumbUpSharp fontSize="inherit" /></div>
            </div>
            <div className="collidetails">
              {x.projects.map((project) => {
                return (
                  <div className="colliproj" key={`${project.id}-${project.phase}`}>
                    <div className="collipname"><span className="collipnumber">{project.number}</span>{project.name}<span className="collipphase">({project.phase})</span></div>
                    <div className="collidel" onClick={() => deleteM.mutate({ bookingIds: project.bookingIds })}><Delete  fontSize="inherit" /></div>
                  </div>
                );
              })}
              {x.vacations.map((vacation) => {
                  return (
                    <div className="colliproj" key={vacation.id}>
                      <div className="collipname">{vacation.name}</div>
                    </div>
                  );
                })}
                
            </div>
            
          </div>
        ) : null}
      </Fragment>
    )))
  );
}
